import React, {useRef, useState} from 'react';
import {
  ScrollView,
  TouchableOpacity,
  Image,
  Text,
  View,
  StyleSheet,
  Platform,
  LayoutChangeEvent,
  NativeSyntheticEvent,
  NativeScrollEvent,
} from 'react-native';
import Feather from 'react-native-vector-icons/Feather';

export interface PillItem {
  key: string;
  label: string;
  imageUrl?: string | null;
}

interface PillRowProps {
  items: PillItem[];
  /** Multi-select mode (brand pills). */
  selectedKeys?: string[];
  onSelectMulti?: (keys: string[]) => void;
  /** Single-select mode (category pills). */
  selectedKey?: string | null;
  onSelect?: (key: string | null) => void;
  withImage?: boolean;
}

const isWeb = Platform.OS === 'web';
/** How much of the visible width to advance per arrow click. */
const SCROLL_STEP_RATIO = 0.8;

const PillRow = ({
  items,
  selectedKeys,
  onSelectMulti,
  selectedKey,
  onSelect,
  withImage = false,
}: PillRowProps) => {
  const scrollRef = useRef<ScrollView>(null);
  const offsetRef = useRef(0);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);
  const [offset, setOffset] = useState(0);

  if (!items || items.length === 0) return null;

  const isMulti = selectedKeys !== undefined && onSelectMulti !== undefined;

  const handlePress = (key: string) => {
    if (isMulti) {
      const next = selectedKeys.includes(key)
        ? selectedKeys.filter(k => k !== key)
        : [...selectedKeys, key];
      onSelectMulti(next);
    } else {
      onSelect?.(selectedKey === key ? null : key);
    }
  };

  const handleLayout = (e: LayoutChangeEvent) => {
    setViewportWidth(e.nativeEvent.layout.width);
  };

  const handleScroll = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    const x = e.nativeEvent.contentOffset.x;
    offsetRef.current = x;
    setOffset(x);
  };

  const maxOffset = Math.max(0, contentWidth - viewportWidth);
  const canScrollLeft = isWeb && offset > 1;
  const canScrollRight = isWeb && offset < maxOffset - 1;

  const scrollBy = (direction: 1 | -1) => {
    const step = (viewportWidth || 0) * SCROLL_STEP_RATIO;
    const target = Math.max(
      0,
      Math.min(maxOffset, offsetRef.current + direction * step),
    );
    scrollRef.current?.scrollTo({x: target, animated: true});
  };

  return (
    <View style={styles.wrapper}>
      <ScrollView
        ref={scrollRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        onLayout={handleLayout}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        onContentSizeChange={(w: number) => setContentWidth(w)}
        contentContainerStyle={styles.container}>
        {items.map(item => {
          const isSelected = isMulti
            ? selectedKeys.includes(item.key)
            : selectedKey === item.key;
          return (
            <TouchableOpacity
              key={item.key}
              onPress={() => handlePress(item.key)}
              style={[
                withImage ? styles.pillWithImage : styles.pill,
                isSelected && styles.pillSelected,
              ]}
              activeOpacity={0.75}>
              {withImage &&
                (item.imageUrl ? (
                  <Image
                    source={{uri: item.imageUrl}}
                    style={styles.logo}
                    resizeMode="contain"
                  />
                ) : (
                  <View style={[styles.logo, styles.logoPlaceholder]} />
                ))}
              <Text
                numberOfLines={1}
                style={[styles.label, isSelected && styles.labelSelected]}>
                {item.label}
              </Text>
            </TouchableOpacity>
          );
        })}
      </ScrollView>

      {canScrollLeft && (
        <TouchableOpacity
          accessibilityLabel="Ver marcas anteriores"
          onPress={() => scrollBy(-1)}
          activeOpacity={0.85}
          style={[styles.arrow, styles.arrowLeft]}>
          <Feather name="chevron-left" size={22} color="#333" />
        </TouchableOpacity>
      )}
      {canScrollRight && (
        <TouchableOpacity
          accessibilityLabel="Ver mais marcas"
          onPress={() => scrollBy(1)}
          activeOpacity={0.85}
          style={[styles.arrow, styles.arrowRight]}>
          <Feather name="chevron-right" size={22} color="#333" />
        </TouchableOpacity>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
    justifyContent: 'center',
  },
  container: {
    paddingHorizontal: 12,
    gap: 8,
    flexDirection: 'row',
    alignItems: 'center',
  },
  pill: {
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 7,
    borderRadius: 20,
    borderWidth: 1.5,
    borderColor: '#E0E0E0',
    backgroundColor: '#FAFAFA',
  },
  pillWithImage: {
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 20,
    borderWidth: 1.5,
    borderColor: '#E0E0E0',
    backgroundColor: '#FAFAFA',
    flexDirection: 'row',
    gap: 7,
    minWidth: 80,
    maxWidth: 140,
  },
  pillSelected: {
    borderColor: '#DD0B1C',
    backgroundColor: '#FFF0F0',
  },
  logo: {
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: '#F0F0F0',
  },
  logoPlaceholder: {
    backgroundColor: '#E0E0E0',
  },
  label: {
    fontSize: 13,
    color: '#555',
    flexShrink: 1,
    fontWeight: '500',
  },
  labelSelected: {
    color: '#DD0B1C',
    fontWeight: '700',
  },
  arrow: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 40,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.95)',
    // @ts-ignore web-only cursor
    cursor: 'pointer',
  },
  arrowLeft: {
    left: 0,
    shadowColor: '#000',
    shadowOffset: {width: 4, height: 0},
    shadowOpacity: 0.08,
    shadowRadius: 6,
  },
  arrowRight: {
    right: 0,
    shadowColor: '#000',
    shadowOffset: {width: -4, height: 0},
    shadowOpacity: 0.08,
    shadowRadius: 6,
  },
});

export default React.memo(PillRow);
